import { BaseService } from './base.service';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';
import { AppSettings } from '../app.settings';
import { ToastrService } from 'ngx-toastr';


import { clone } from 'lodash-es';
import { each } from 'lodash-es';
import { Response } from '../models/response.model';
import { Order } from '../models/order.model';
import { Product } from '../models/product.model';
import { ProductCatalog } from '../models/product-catalog.model';
import { ProductService } from './product.service';
import { escapeRegExp, isEmptyArray } from '../shared/utils';
import { LocalComponentStore } from '../store/local-component.store';
import { AutoShipItem } from '../models/auto-ship.model';
import { BRANCH_TYPE, ECOMMERCE_TYPE, PREORDER_TYPE, SUMMARY_TYPE } from '../constants/order-types';
import { OrderService } from './order.service';
import { IOrderSuggestion } from '../interfaces/order-suggestion.type';
import { AuthService } from './auth.service';
import { PageService } from './page.service';
import { OrderHistory } from '../models/order-history.model';
import { AppDate } from '../models/date.model';
import { AvailableOrderWindow } from '../interfaces/order-window.interface';
import { MultiTenantService } from './multi-tenant.service';
import { ConfigService } from './config.service';
import { API_URL } from '../constants/api-urls';

const DEFAULT_LIMIT = 20;
const MIN_SYMBOLS_TO_SEARCH = 3;

const ORDER_SEARCH_LIMIT = 10;

export interface OrderCategory {
    type: string;
    label: string;
    total: number;
    orders: OrderHistory[];
    selected: boolean;
    fetching: boolean;
    sorting: boolean
}

export interface SearchParam {
    name: string;
    label: string;
    value?: string;
    selected: boolean
}

export const ALL_SEARCH_OPTIONS: SearchParam[] = [
    { name: 'order_id', label: 'Order #', value: '', selected: false },
    { name: 'sku_number', label: 'Product #', value: '', selected: false },
    { name: 'product_name', label: 'Product Name', value: '', selected: false },
    { name: 'window_id', label: 'Window Name', value: '', selected: false },
    { name: 'purchaser', label: 'Purchaser', value: '', selected: false },
    { name: 'cart_name', label: 'Order Name', value: '', selected: false },
    { name: 'sap', label: 'SAP #', value: '', selected: false },
    { name: 'tracking', label: 'Tracking #', value: '', selected: false },
];


const ORDER_HISTORY_TYPES = {
    PREORDER_TYPE: 'OW',
    ECOMMERCE_TYPE: 'OD',
    SUMMARY_TYPE: 'AB',
    BRANCH_TYPE: 'BR',
}

interface SearchState {
    searchingOrderHistory: boolean;
    searchingRecentOrders: boolean;
    orderCategories: OrderCategory[];
    recentOnDemandOrders: OrderHistory[];
    foundProducts: Product[];
    totalProducts: number;
    searchingProducts: boolean;
    sortedColumn: { column: string; direction: string };
    searchPerformed: boolean;
    initialOrderSearchPerformed: boolean;
}

export interface OrderSearchParam {
    name: string;
    value?: string;
    dates?: {dates_type: string; start: string; end: string};
}

@Injectable()
export class SearchService extends BaseService {

    public searchParams: SearchParam[] = []

    public selectedSearchParam: OrderSearchParam;

    public globalTerm: string;
    public currentBuyingWindowId = 0;
    public selectedOrderType: string;

    private searchOffset = 0;

    private componentStore = new LocalComponentStore<SearchState>({
        searchingOrderHistory: false,
        searchingRecentOrders: false,
        orderCategories: [],
        recentOnDemandOrders: [],
        foundProducts: [],
        totalProducts: 0,
        searchingProducts: false,
        sortedColumn: { column: 'order_id', direction: 'DESC' },
        searchPerformed: false,
        initialOrderSearchPerformed: false,
    });


    /** Synchronous accessors preserve the previous public observable fields. */
    get searchingOrderHistory(): boolean { return this.componentStore.snapshot(s => s.searchingOrderHistory); }
    set searchingOrderHistory(v: boolean) { this.componentStore.patchState({ searchingOrderHistory: v }); }

    get searchingRecentOrders(): boolean { return this.componentStore.snapshot(s => s.searchingRecentOrders); }
    set searchingRecentOrders(v: boolean) { this.componentStore.patchState({ searchingRecentOrders: v }); }

    get orderCategories(): OrderCategory[] { return this.componentStore.snapshot(s => s.orderCategories); }
    set orderCategories(v: OrderCategory[]) { this.componentStore.patchState({ orderCategories: v }); }

    get recentOnDemandOrders(): OrderHistory[] { return this.componentStore.snapshot(s => s.recentOnDemandOrders); }
    set recentOnDemandOrders(v: OrderHistory[]) { this.componentStore.patchState({ recentOnDemandOrders: v }); }

    get foundProducts(): Product[] { return this.componentStore.snapshot(s => s.foundProducts); }
    set foundProducts(v: Product[]) { this.componentStore.patchState({ foundProducts: v }); }

    get totalProducts(): number { return this.componentStore.snapshot(s => s.totalProducts); }
    set totalProducts(v: number) { this.componentStore.patchState({ totalProducts: v }); }

    get searchingProducts(): boolean { return this.componentStore.snapshot(s => s.searchingProducts); }
    set searchingProducts(v: boolean) { this.componentStore.patchState({ searchingProducts: v }); }

    get sortedColumn(): { column: string; direction: string } { return this.componentStore.snapshot(s => s.sortedColumn); }
    set sortedColumn(v: { column: string; direction: string }) { this.componentStore.patchState({ sortedColumn: v }); }

    get searchPerformed(): boolean { return this.componentStore.snapshot(s => s.searchPerformed); }
    set searchPerformed(v: boolean) { this.componentStore.patchState({ searchPerformed: v }); }

    get initialOrderSearchPerformed(): boolean { return this.componentStore.snapshot(s => s.initialOrderSearchPerformed); }
    set initialOrderSearchPerformed(v: boolean) { this.componentStore.patchState({ initialOrderSearchPerformed: v }); }

    public get canLoadMore(): boolean {
        if (this.searchingProducts) {
            return false;
        }
        return this.totalProducts > this.foundProducts.length;
    }

    // private foundProductIdCache = new CacheModel(TEN_MINUTES);


    constructor(
        protected http: HttpClient,
        protected appSettings: AppSettings,
        protected toastr: ToastrService,
        private productService: ProductService,
        private orderService: OrderService,
        private authService: AuthService,
        private pageService: PageService,
        private multiTenantService: MultiTenantService,
        private config: ConfigService
    ) {
        super('/search', http, appSettings, toastr);
        this.initOrderSearch();
    }

    setTotalProducts(val: number) {
        this.totalProducts = val;
    }

    setFoundProducts(val: Product[], append = false) {
        if (!val) {
            return;
        }
        if (append) {
            this.foundProducts = [...this.foundProducts, ...val];
        } else {
            this.foundProducts = val;
        }

    }


    public productsSearch(options: {
        limit?: number, offset?: number, loadMore?: boolean,
        refresh?: boolean, entity_id?: boolean
    } = {})
        : Observable<(Product | AutoShipItem)[]> {

        if (!this.globalTerm || this.globalTerm.length < MIN_SYMBOLS_TO_SEARCH) {
            return of([]);
        }

        if (!options.loadMore) {
            this.setTotalProducts(0);
            this.setFoundProducts([]);
            this.searchOffset = 0;
        }
        const params = {
            match: this.globalTerm,
            window_id: this.currentBuyingWindowId
        };

        if (options.entity_id) {
            params['entity_id'] = options.entity_id;
        }

        let limit = options.limit || DEFAULT_LIMIT;
        let offset = options.offset || 0;
        if (options.loadMore) {
            offset = this.searchOffset;
        }

        if (options.refresh) {
            limit = this.foundProducts.length;
            offset = 0;
        }

        this.searchingProducts = true;
        this.searchPerformed = false;

        // const v2ApiUrl = `${API_URL}/products/search`;
        const v2ApiUrl = `${API_URL}/es/products/search`;
        this.orderService.currentOrderTypeLabel = this.orderService.currentOrderType;

        return this.http.post<HttpResponse<Response>>(v2ApiUrl, params,
            {
                observe: 'response',
                headers: new HttpHeaders({ 'Range': `${limit}-${offset}`, 'x-xsrf-token': '' })
            }).pipe(switchMap(httpResponse => {
                const count = parseInt(httpResponse.headers.get('X-Count'), 10);

                this.setTotalProducts(count);
                const response = new Response(httpResponse.body);
                if (isEmptyArray(response.data)) {
                    return of([]);
                }

                const products: Product[] = response.data.map(i => new Product(i));
                this.searchOffset = offset + products.length;
                return of(products);
            }),
                catchError(this.handleError('SearchService::productsSearch', [])),
                tap((result) => {
                    this.searchingProducts = false;
                    this.searchPerformed = true;
                    this.setFoundProducts(result, options.loadMore);
                })
            );
    }

    public highlight(str) {

        if (!this.globalTerm) {
            return;
        }

        const safeStr = str.replace(/&amp;/gm, '&');
        const searchPattern = escapeRegExp(this.globalTerm); // escape special characters
        const pattern = new RegExp('(' + searchPattern + ')', 'gi');
        return safeStr.replace(pattern, match => `<strong>${match}</strong>`);
    }

    public orderSuggestions(term: string, filter: Record<string, unknown> = null): Observable<IOrderSuggestion> {
        const resultKey: string = btoa(JSON.stringify({ k: term, f: filter })).replace('=', '');

        if (this.cache.has(resultKey)) {
            return of(this.cache.get(resultKey));
        }

        return this.http.post<Response>(this.apiURL + '/order-suggestions', { term: term, filter: filter },
            { headers: this.xsrfTokenHeader })
            .pipe(
                map(response => {
                    const result = response.data;

                    this.cache.set(resultKey, result);

                    return result;
                }),
                catchError(this.handleError('SearchService::orderSuggestions', {}))
            );
    }

    public results(type: string, key: string, filter: Record<string, unknown> = null, paging: string = '18-0'): Observable<Order[]> {
        // const filterKey: string = btoa(JSON.stringify({t: type, k: key})).replace('=', '');
        const resultKey: string = btoa(JSON.stringify({ t: type, k: key, f: filter, p: paging })).replace('=', '');
        // const eTag: string = this.cache.getETag(filterKey);
        const headers: HttpHeaders = new HttpHeaders({ 'Range': paging });
        const data = { key: key, filter: filter };

        // if (this.cache.has(resultKey)) {
        //     return of(this.cache.get(resultKey));
        // }

        switch (type) {
            case 'order':
                return this.getOrders(resultKey, data, headers);
        }
    }

    public getCatalogs(key: string, filterKey: string, data: Record<string, unknown>, headers: HttpHeaders): Observable<ProductCatalog[]> {
        return this.http.post<HttpResponse<Response>>(
            this.apiURL + '/program/results', data, { observe: 'response', headers: headers }
        ).pipe(
            map(httpResponse => {
                const response = new Response(httpResponse.body);
                const result = response.data;

                // this.cacheFilters(filterKey, result);

                this.productService.catalogCache.set(key, clone(result.items));

                each(result.items, (id, i) => {
                    if (this.productService.catalogCache.has(id)) {
                        result.items[i] = new ProductCatalog(this.productService.catalogCache.get(id));
                    }
                });

                return result.items;
            }),
            catchError(this.handleError('SearchService::getCatalogs', []))
        );
    }

    public getProducts(type: string, key: string, filterKey: string, data: Record<string, unknown>, headers: HttpHeaders): Observable<Product[]> {
        return this.http.post<HttpResponse<Response>>(
            this.apiURL + '/' + type + '/results', data, { observe: 'response', headers: headers }
        ).pipe(
            map(httpResponse => {
                const response = new Response(httpResponse.body);
                const result = response.data;

                this.productService.productCache.set(key, clone(result.items));

                each(result.items, (id, i) => {
                    if (this.productService.productCache.has(id)) {
                        result.items[i] = new Product(this.productService.productCache.get(id));
                    }
                });

                return result.items;
            }),
            catchError(this.handleError('SearchService::getProducts', []))
        );
    }

    public getOrders(key: string, data: Record<string, unknown>, headers: HttpHeaders): Observable<Order[]> {
        return this.http.post<HttpResponse<Response>>(
            this.apiURL + '/order/results', data, { observe: 'response', headers: headers }
        ).pipe(
            map(httpResponse => {
                const response = new Response(httpResponse.body);
                const result = response.data;

                each(result, (order, i) => {
                    result[i] = new Order(order);
                });

                return result;
            }),
            catchError(this.handleError('SearchService::getOrders', []))
        );
    }

    public clearSearchResults() {
        this.globalTerm = '';
        this.foundProducts = [];
        this.totalProducts = 0;
        this.searchPerformed = false;
        this.searchOffset = 0;
    }

    public get hasProducts(): boolean {
        return this.foundProducts && this.foundProducts.length > 0;
    }

    public setCurrentOrderWindowId(val: number) {
        this.currentBuyingWindowId = val;
        this.selectedOrderType = this.currentBuyingWindowId > 0 ? PREORDER_TYPE : ECOMMERCE_TYPE;
    }


    private initOrderSearch() {

        this.sortedColumn = { column: 'order_id', direction: 'DESC' };
        this.selectedSearchParam = null;
        this.searchParams = ALL_SEARCH_OPTIONS.filter(o => {
            // remove sap from search options if user can't have access to it
            if (!this.authService.canShowSAP && this.config.showBillingInfo && o.name === 'sap') {
                return false;
            }

            // remove window from search options if user can't have access to it
            if (!this.authService.canBuyingWindow && o.name === 'window_id') {
                return false;
            }

            return true;
        });

        const categories: OrderCategory[] = [];

        if (this.authService.canBuyingWindow && this.multiTenantService.showOrderWindowLabel) {
            categories.push({
                type: PREORDER_TYPE, label: this.multiTenantService.orderWindowLabel, total: 0,
                orders: [], fetching: false, selected: false, sorting: false
            });
        }

        if (this.authService.isWholeSaler || this.authService.isWholeSalerMultiple) {
            categories.push({
                type: SUMMARY_TYPE, total: 0, label: `${this.multiTenantService.projectLabel} Orders`,
                orders: [], fetching: false, selected: false, sorting: false
            });
        }

        if (this.authService.isWholeSalerMultiple) {
            categories.push({
                type: BRANCH_TYPE, total: 0, label: 'Branch Orders',
                orders: [], fetching: false, selected: false, sorting: false
            });
        }

        if (this.multiTenantService.showOnDemandLabel) {
            categories.push({
                type: ECOMMERCE_TYPE, total: 0, label: this.multiTenantService.onDemandLabel,
                orders: [], fetching: false, selected: false, sorting: false
            });
        }

        const selectedTab = this.pageService.getSelectedOrderHistoryTab();
        if (selectedTab) {
            categories.forEach(s => {
                s.selected = s.type === selectedTab;
            });

        }

        // check if one of category is selected && orderCategories are not empty
        const hasSelectedCategory = categories.some(oc => oc.selected);
        if (!isEmptyArray(categories) && !hasSelectedCategory) {
            categories[0].selected = true;
        }

        // publish the fully-built list once so orderCategories$ emits
        this.orderCategories = categories;
    }


    private getFilters(type: string, force = false) {
        const filter = { type: ORDER_HISTORY_TYPES[type] };
        if (this.selectedSearchParam && !force) {
            filter['search_params'] = this.selectedSearchParam;
        }

        if (this.sortedColumn.column) {
            filter['sort'] = { column: this.sortedColumn.column, direction: this.sortedColumn.direction };
        }
        return filter;
    }


    public searchOrders(searchParams: OrderSearchParam = null): void {
        if (searchParams) {
            this.selectedSearchParam = searchParams;
        }

        this.searchingOrderHistory = true;

        const requests = this.orderCategories.map(category => {
            const paging = `${ORDER_SEARCH_LIMIT}-0`;

            return this.orderService.getOrderHistory(
                this.getFilters(category.type),
                paging
            );
        });

        forkJoin(requests).pipe(
            finalize(() => {
                this.searchingOrderHistory = false;
            })
        ).subscribe({
            next: resultList => {
                resultList.forEach(result => {
                    const mappedType =
                        this.getMappedOrderHistoryType(result.type);

                    this.orderCategories = this.orderCategories.map(category => {
                        if (category.type !== mappedType) {
                            return category;
                        }

                        return {
                            ...category,
                            orders: result.orders,
                            total: result.count
                        };
                    });
                });

                this.initialOrderSearchPerformed = true;
            },
            error: error => {
                console.error(
                    'SearchService::searchOrders failed',
                    error
                );
            }
        });
    }

    public searchRecentODOrders(limit = 4): Observable<{
        orders: OrderHistory[];
        count: number;
        type: string;
    }> {
        this.searchingRecentOrders = true;
        this.recentOnDemandOrders = [];

        const paging = `${limit}-0`;

        const orderingType =
            this.multiTenantService.showOnDemandLabel
                ? ECOMMERCE_TYPE
                : PREORDER_TYPE;

        const filters = this.getFilters(orderingType, true);
        filters['user_only'] = true;

        const fallbackResult: {
            orders: OrderHistory[];
            count: number;
            type: string;
        } = {
            orders: [],
            count: 0,
            type: orderingType
        };

        return this.orderService
            .getOrderHistory(filters, paging)
            .pipe(
                tap(result => {
                    this.recentOnDemandOrders = result?.orders || [];
                }),
                catchError(error => {
                    console.error(
                        'SearchService::searchRecentODOrders failed',
                        error
                    );

                    return of(fallbackResult);
                }),
                finalize(() => {
                    this.searchingRecentOrders = false;
                })
            );
    }

    public loadMore(type: string) {
        const ordersType = this.orderCategories.find(s => s.type === type);
        if (!ordersType) {
            return;
        }

        if (ordersType.total === ordersType.orders.length) {
            return;
        }


        const paging = `${ORDER_SEARCH_LIMIT}-${ordersType.orders.length}`;
        ordersType.fetching = true;

        this.orderService.getOrderHistory(this.getFilters(ordersType.type), paging).subscribe(result => {
            this.orderCategories = this.orderCategories.map(s => {
                const returnedType =
                    this.getMappedOrderHistoryType(result.type);

                if (s.type !== returnedType) {
                    return {
                        ...s,
                        fetching: false
                    };
                }

                return {
                    ...s,
                    fetching: false,
                    orders: [...s.orders, ...result.orders]
                };
            });
        });

    }

    public sortOrders(column: string) {
        const direction = this.sortedColumn.direction === 'ASC' ? 'DESC' : 'ASC'
        this.sortedColumn = { column, direction };

        forkJoin(this.orderCategories.map(oc => {
            return this.sort(oc);
        })).subscribe(result => {
            this.orderCategories = result.map(r => ({
                ...r,
                sorting: false
            }));
        });
    }


    public exportToExcel(type: string) {
        const params = btoa(JSON.stringify(this.getFilters(type)));
        window.open(`${API_URL}/orders/spreadsheet?params=${encodeURIComponent(params)}`, '_blank');
    }

    private getOrderValueByColumnsName(order: OrderHistory, columnName: string) {
        switch (columnName) {
            case 'order_id':
                return order.id;
            case 'state':
                return order.state;
            case 'order_date':
                return order.order_date;
            case 'purchaser':
                return order.purchaser;
            case 'cart_name':
                return order.cart_name;
            case 'items_count':
                return order.items_count;
            case 'order_total':
                return order.order_total;
            default:
                return null;
        }
    }

    private internalSort(order1: OrderHistory, order2: OrderHistory, columnName: string, dir: string): number {
        const value1 = this.getOrderValueByColumnsName(order1, columnName);
        const value2 = this.getOrderValueByColumnsName(order2, columnName);

        if (typeof value1 === 'string' && typeof value2 === 'string') {
            return dir === 'ASC' ? value1.localeCompare(value2) : value2.localeCompare(value1);
        }

        if (typeof value1 === 'number' && typeof value2 === 'number') {
            return dir === 'ASC' ? value1 - value2 : value2 - value1;
        }

        if (columnName === 'order_date') {

            if (!value1 && !value2) {
                return 0;
            }

            if (dir === 'ASC') {
                if (!value1) {
                    return -1;
                }
                if (!value2) {
                    return 1;
                }
                return (value1 as AppDate).isBefore(value2) ? -1 : 1;

            } else {
                if (!value2) {
                    return -1;
                }
                if (!value1) {
                    return 1;
                }
                return (value2 as AppDate).isBefore(value1) ? -1 : 1;

            }

        }


        return 0;
    }


    private sort(orderCategory: OrderCategory): Observable<OrderCategory> {
        if (orderCategory.total > orderCategory.orders.length) {
            // sorting on server
            const paging = `${orderCategory.orders.length}-0`;
            orderCategory.fetching = true;

            this.orderService.getOrderHistory(this.getFilters(orderCategory.type), paging).subscribe(result => {
                this.orderCategories = this.orderCategories.map(s => {
                    const returnedType =
                        this.getMappedOrderHistoryType(result.type);

                    if (s.type !== returnedType) {
                        return {
                            ...s,
                            fetching: false
                        };
                    }

                    return {
                        ...s,
                        fetching: false,
                        orders: result.orders
                    };
                });
            });
        } else {
            // internal sorting
            const orders = [...orderCategory.orders].sort((o1, o2) => {
                return this.internalSort(
                    o1,
                    o2,
                    this.sortedColumn.column,
                    this.sortedColumn.direction
                );
            });

            return of({
                ...orderCategory,
                orders
            });
        }
        return of(orderCategory);
    }



    public selectedOrdersType(type: string) {
        this.pageService.setSelectedOrderHistoryTab(type);
        this.orderCategories = this.orderCategories.map(s => {
            return { ...s, selected: s.type === type }
        })
    }


    public getBuyingWindows(): Observable<AvailableOrderWindow[]> {
        return this.orderService.getAvailableOrderWindows();
    }


    public resetSearch() {
        this.initOrderSearch();
        this.searchOrders();
    }

    public get selectedOrderCategory() {
        return this.orderCategories.find(oc => oc.selected);
    }


    public get onDemandOrdersHistory() {
        return this.orderCategories.find(category => category.type === ECOMMERCE_TYPE)?.orders || [];
    }

    get selectedOrders(): OrderHistory[] {
        return this.selectedOrderCategory?.orders || [];
    }

    private getMappedOrderHistoryType(type: string): string {
        for (const [key, value] of Object.entries(ORDER_HISTORY_TYPES)) {
            if (value === type) {
                return key;
            }
        }
    }
}
